import {otsBaseApi} from "../../ots-api";
import {Permission} from "../../../model/permission";

const taggedBaseApi = otsBaseApi.enhanceEndpoints({addTagTypes: ["Permissions", "UserPermissions", "RolePermissions", "PermissionABACPolicies"]});

const permissionApi = taggedBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllPermissions: builder.query<
      Permission[],
      void
    >({
      query: () => ({
        url: `/permissions/all`,
        method: 'GET',
      }),
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "Permissions", id} as const)),
            {type: "Permissions", id: "LIST"}
          ];
        } else {
          return [{type: "Permissions", id: "LIST"}];
        }
      },
    }),
    getUserPermissions: builder.query<
      Permission[],
      void
    >({
      query: () => ({
        url: `/permissions`,
        method: 'GET',
      }),
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "Permissions", id} as const)),
            {type: "UserPermissions", id: "LIST"}
          ];
        } else {
          return [{type: "UserPermissions", id: "LIST"}];
        }
      },
    }),
    getPermissionsByRole: builder.query<
      Permission[],
      string
    >({
      query: (arg) => ({
        url: `/permissions/all`,
        method: 'GET',
        params: {
          roleId: arg,
        }
      }),
      providesTags: (result, error, arg) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "Permissions", id} as const)),
            {type: "RolePermissions", id: arg}
          ];
        } else {
          return [{type: "RolePermissions", id: arg}];
        }
      },
    }),
    getPermission: builder.query<Permission, string>({
      query: (id) => ({
        url: `/permissions/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{type: "Permissions", id}]
    }),
    createPermission: builder.mutation<Permission, Partial<Permission>>({
      query: (mapping) => ({
        url: "/permissions",
        method: "POST",
        body: mapping
      }),
      invalidatesTags: [{type: "Permissions", id: "LIST"}]
    }),
    updatePermission: builder.mutation<Permission, Partial<Permission>>({
      query: (mapping) => ({
        url: "/permissions",
        method: "PUT",
        body: mapping,
      }),
      invalidatesTags: (result, error, {id}) => [
        {type: "Permissions", id},
      ]
    }),
    deletePermission: builder.mutation<void, Partial<Permission>>({
      query: (mapping) => ({
        url: "/permissions",
        method: "DELETE",
        body: mapping
      }),
      invalidatesTags: (result, error, {id}) => [{type: "Permissions", id}]
    }),
    addABACPolicyToPermission: builder.mutation<void, { permissionId: string, abacPolicyId: string }>({
      query: (arg) => ({
        url: `/permissions/${arg.permissionId}/abac-policies`,
        method: "POST",
        params: {
          abacPolicyId: arg.abacPolicyId,
        }
      }),
      invalidatesTags: (result, error, {permissionId}) => [{type: "PermissionABACPolicies", id: permissionId}]
    }),
    removeABACPolicyFromPermission: builder.mutation<void, { permissionId: string, abacPolicyId: string }>({
      query: (arg) => ({
        url: `/permissions/${arg.permissionId}/abac-policies/${arg.abacPolicyId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, {permissionId}) => [{type: "PermissionABACPolicies", id: permissionId}]
    }),
  })
});

export const {
  useGetAllPermissionsQuery,
  useGetUserPermissionsQuery,
  useGetPermissionsByRoleQuery,
  useGetPermissionQuery,
  useCreatePermissionMutation,
  useUpdatePermissionMutation,
  useDeletePermissionMutation,
  useAddABACPolicyToPermissionMutation,
  useRemoveABACPolicyFromPermissionMutation,
} = permissionApi;
